import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Loader2, Truck, MapPin, Phone, Clock, Home, LogOut, RefreshCw, Wallet, CreditCard, CheckCircle2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;
const TOKEN_KEY = "runner_token";

function client() {
  const t = localStorage.getItem(TOKEN_KEY);
  return axios.create({ baseURL: API, headers: t ? { Authorization: `Bearer ${t}` } : {} });
}

/**
 * /runner — dedicated deliveries dashboard.
 * If not authenticated, shows the password prompt inline.
 */
export default function RunnerPage() {
  const nav = useNavigate();
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Verify existing token; if invalid, drop it.
    if (authed) {
      client().get("/runner/verify").catch(() => {
        localStorage.removeItem(TOKEN_KEY); setAuthed(false);
      });
    }
  }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/runner/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setAuthed(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Accesso non riuscito");
    } finally { setBusy(false); }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#2b1410] text-[#f7ebdf] flex items-center justify-center px-4">
        <form onSubmit={login} className="w-full max-w-sm rounded-2xl bg-[#3a1b18] p-6 space-y-4">
          <div className="text-center">
            <Truck className="h-10 w-10 mx-auto text-[#f7ebdf]/80" />
            <h1 className="mt-2 font-serif text-2xl font-bold">Consegne · Il Pozzo</h1>
            <p className="text-sm text-[#f7ebdf]/70">Accedi con la password fornita dall'admin</p>
          </div>
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="runner-password"
            className="w-full rounded-lg border border-white/20 bg-white/10 text-[#f7ebdf] px-3 py-2 placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#f7ebdf]/30"
            placeholder="Password fattorino"
            autoFocus
          />
          {error && <div className="text-sm text-red-300">{error}</div>}
          <button type="submit" disabled={busy || !password} data-testid="runner-login-submit"
            className="w-full rounded-full bg-[#f7ebdf] text-[#2b1410] px-4 py-2 font-semibold disabled:opacity-50">
            {busy ? <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> : null}
            Entra
          </button>
        </form>
      </div>
    );
  }

  return <RunnerDashboard onLogout={logout} />;
}

function RunnerDashboard({ onLogout }) {
  const [data, setData] = useState({ deliveries: [], count: 0, date: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client().get("/runner/deliveries");
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); window.location.reload(); return; }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  const mark = async (order, status) => {
    try {
      await client().patch(`/runner/deliveries/${order.id}`, { status });
      load();
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-[#faf3ee] pb-20">
      <header className="sticky top-0 z-30 bg-[#2b1410] text-[#f7ebdf] shadow-sm pt-[env(safe-area-inset-top)]">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <div>
            <div className="font-serif text-lg font-bold leading-none">Consegne</div>
            <div className="text-[11px] text-[#f7ebdf]/70 uppercase tracking-widest">{data.date || "…"}</div>
          </div>
          <div className="ml-auto flex gap-2">
            <button onClick={load} className="rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs inline-flex items-center gap-1">
              <RefreshCw className="h-3.5 w-3.5" /> Aggiorna
            </button>
            <button onClick={onLogout} className="rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs inline-flex items-center gap-1">
              <LogOut className="h-3.5 w-3.5" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4">
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" /></div>
        ) : data.count === 0 ? (
          <div className="rounded-xl bg-white border border-[#e8dcc8] p-10 text-center text-[#7c5a4e]">
            Nessuna consegna in programma. Bella serata! 🌙
          </div>
        ) : (
          <div className="space-y-3">
            {data.deliveries.map((o) => (
              <DeliveryCard key={o.id} order={o} onMark={(s) => mark(o, s)} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function DeliveryCard({ order, onMark }) {
  const isDone = ["delivered", "failed"].includes(order.status);
  const isCash = order.payment_status !== "paid";
  return (
    <div className={`rounded-xl bg-white border border-[#e8dcc8] p-4 ${isDone ? "opacity-60" : ""}`} data-testid={`runner-delivery-${order.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-serif text-lg font-bold text-[#2b1410]">{order.name}</div>
            {order._source === "shop" && <span className="text-[10px] rounded-full bg-purple-100 text-purple-800 px-2 py-0.5">SHOP</span>}
            <span className="inline-flex items-center gap-1 text-[11px] rounded-full bg-[#faf3ee] px-2 py-0.5">
              <Clock className="h-3 w-3" /> {order.slot || "—"}
            </span>
          </div>
          <a href={`tel:${order.phone}`} className="mt-1 text-sm text-[#4a1c17] font-semibold flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {order.phone}</a>
          <div className="mt-1 text-sm text-[#2b1410] flex items-start gap-1">
            <MapPin className="h-4 w-4 shrink-0 mt-0.5 text-[#4a1c17]" />
            <span>{order.address}</span>
          </div>
          {order.notes && <div className="mt-1 text-xs text-[#7c5a4e] italic">"{order.notes}"</div>}
        </div>
        <div className="text-right shrink-0">
          <div className="text-xl font-bold text-[#4a1c17]">{eur(order.total)}</div>
          <div className="text-[10px] uppercase tracking-widest font-semibold inline-flex items-center gap-1 mt-0.5">
            {isCash ? <><Wallet className="h-3 w-3" /> Da incassare</> : <><CreditCard className="h-3 w-3" /> Già pagato</>}
          </div>
        </div>
      </div>

      <div className="mt-2 pt-2 border-t border-[#e8dcc8] text-xs text-[#7c5a4e]">
        {(order.items || []).map((it, i) => (
          <span key={i}>{it.qty}× {it.name}{i < (order.items || []).length - 1 ? " · " : ""}</span>
        ))}
      </div>

      <a
        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.address)}`}
        target="_blank" rel="noreferrer"
        className="mt-3 inline-flex items-center gap-1 text-xs text-[#4a1c17] underline"
      >
        Apri in Google Maps →
      </a>

      {!isDone && (
        <div className="mt-3 flex gap-2">
          <button onClick={() => onMark("delivered")} data-testid={`runner-mark-delivered-${order.id}`}
            className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 text-sm font-semibold inline-flex items-center justify-center gap-1">
            <CheckCircle2 className="h-4 w-4" /> Consegnato
          </button>
          <button onClick={() => onMark("failed")} className="rounded-full border border-red-300 text-red-700 hover:bg-red-50 px-3 py-2 text-sm font-semibold inline-flex items-center gap-1">
            <XCircle className="h-4 w-4" /> Fallita
          </button>
        </div>
      )}
      {isDone && (
        <div className="mt-3 text-xs font-semibold uppercase tracking-widest">
          {order.status === "delivered" ? <span className="text-emerald-700">✓ Consegnato</span> : <span className="text-red-700">Consegna fallita</span>}
        </div>
      )}
    </div>
  );
}
